import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Save, ChevronRight, Edit2, Package, Receipt, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../api';

// ─── Aperçu PDF — Proposition Commerciale (fidèle à build_devis_pdf) ──────────

function ApercuDevisVente({ champs = {} }) {
  const get = (key, def = '') => (champs[key]?.valeur ?? def);

  // Palette identique à ventes_pdf.py
  const PRIMARY    = '#1A3A6E';
  const GREY_HDR   = '#EEEEEE';
  const GREY_LIGHT = '#F9FAFB';
  const BORDER     = '#CCCCCC';
  const TEXT_DARK  = '#000000';
  const TEXT_MED   = '#444444';
  const TEXT_MUTED = '#888888';

  // Largeurs relatives des colonnes (proportionnelles au PDF ReportLab)
  // W_REF+IMG=3.3cm, W_DES≈6.6cm, W_QTY=1.2cm, W_PUB=2.5cm, W_REM=1.3cm, W_TOT=2.5cm / total≈17.4cm
  const cols = ['19%', '38%', '7%', '14%', '8%', '14%'];

  const tdBase = {
    padding: '4px 5px',
    border: `0.5px solid ${BORDER}`,
    fontSize: 8,
    color: TEXT_DARK,
    verticalAlign: 'top',
  };

  return (
    <div style={{
      background: 'white',
      fontFamily: 'Helvetica, Arial, sans-serif',
      fontSize: 8,
      lineHeight: 1.45,
      border: `1px solid ${BORDER}`,
      borderRadius: 4,
      overflow: 'hidden',
      color: TEXT_DARK,
    }}>

      {/* ══ 1. EN-TÊTE ══ */}
      <div style={{ display: 'flex', alignItems: 'flex-start', padding: '14px 16px 10px', borderBottom: `0.5px solid ${BORDER}` }}>
        {/* Logo — 35% */}
        <div style={{ width: '35%' }}>
          <div style={{
            width: 72, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: '#F3F4F6', border: '1px dashed #D1D5DB', borderRadius: 3,
            fontSize: 7, color: '#9CA3AF', fontWeight: 700, letterSpacing: 1,
          }}>LOGO</div>
        </div>
        {/* Espace central — 20% */}
        <div style={{ width: '20%' }} />
        {/* Titre / N° / Dates — 45%, aligné droite */}
        <div style={{ width: '45%', textAlign: 'right' }}>
          <p style={{ fontWeight: 700, fontSize: 13, color: PRIMARY, marginBottom: 2 }}>PROPOSITION COMMERCIALE</p>
          <p style={{ fontWeight: 700, fontSize: 10, color: PRIMARY, marginBottom: 1 }}>N° VE-2026-XXXX</p>
          <p style={{ color: TEXT_MED, fontSize: 7.5 }}>Date : 07/02/2026</p>
          <p style={{ color: TEXT_MED, fontSize: 7.5 }}>Valable jusqu'au : 09/03/2026</p>
        </div>
      </div>

      <div style={{ padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>

        {/* ══ 2. BLOCS ÉMETTEUR / DESTINATAIRE ══ */}
        <div style={{ display: 'flex', gap: '20%' }}>
          {/* Émetteur — 40% */}
          <div style={{ width: '40%', border: `0.5px solid ${BORDER}`, padding: '5px 7px' }}>
            <p style={{ fontWeight: 700, fontSize: 8.5, marginBottom: 2 }}>WIMAT GUADELOUPE SAS</p>
            <p style={{ color: TEXT_MED, fontSize: 7.5 }}>ZI de Jarry — Bâtiment B</p>
            <p style={{ color: TEXT_MED, fontSize: 7.5 }}>97122 Baie-Mahault</p>
            <p style={{ color: TEXT_MED, fontSize: 7.5 }}>0590 XX XX XX</p>
          </div>
          {/* Destinataire — 40% */}
          <div style={{ width: '40%', border: `0.5px solid ${BORDER}`, padding: '5px 7px' }}>
            <p style={{ fontWeight: 700, fontSize: 8.5, marginBottom: 2 }}>KANNORD SAS</p>
            <p style={{ color: TEXT_MED, fontSize: 7.5 }}>5 Av. du Commerce</p>
            <p style={{ color: TEXT_MED, fontSize: 7.5 }}>97200 Fort-de-France</p>
          </div>
        </div>

        {/* Blocs contacts (sous les blocs principaux) */}
        <div style={{ display: 'flex', gap: '20%' }}>
          <div style={{ width: '40%', border: `0.5px solid ${BORDER}`, padding: '4px 7px' }}>
            <p style={{ fontWeight: 700, fontSize: 7.5, marginBottom: 1 }}>Contact</p>
            <p style={{ color: TEXT_MED, fontSize: 7.5 }}>Jean Dupont</p>
            <p style={{ color: TEXT_MED, fontSize: 7.5 }}>j.dupont@wimat.fr</p>
          </div>
          <div style={{ width: '40%', border: `0.5px solid ${BORDER}`, padding: '4px 7px' }}>
            <p style={{ fontWeight: 700, fontSize: 7.5, marginBottom: 1 }}>Contact</p>
            <p style={{ color: TEXT_MED, fontSize: 7.5 }}>Marie Martin</p>
            <p style={{ color: TEXT_MED, fontSize: 7.5 }}>m.martin@kannord.fr</p>
          </div>
        </div>

        {/* ══ 3. INTRODUCTION — zone éditable (surbrillance jaune) ══ */}
        <div style={{ background: '#FFFBEB', border: '1.5px dashed #F59E0B', padding: '5px 8px', borderRadius: 3 }}>
          <p style={{ fontStyle: 'italic', color: TEXT_MED, fontSize: 7.5 }}>
            {get('texte_introduction', 'Veuillez trouver ci-dessous notre proposition commerciale.')}
          </p>
        </div>

        {/* ══ 4. TABLEAU DES LIGNES ══ */}
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>
            {cols.map((w, i) => <col key={i} style={{ width: w }} />)}
          </colgroup>
          <thead>
            <tr style={{ background: GREY_HDR }}>
              {[
                { label: 'Référence', align: 'center' },
                { label: 'Désignation', align: 'left' },
                { label: 'Qté', align: 'center' },
                { label: 'PU Brut', align: 'center' },
                { label: 'Rem.', align: 'center' },
                { label: 'Total HT', align: 'center' },
              ].map(({ label, align }) => (
                <th key={label} style={{ ...tdBase, fontWeight: 700, textAlign: align, background: GREY_HDR }}>
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Ligne article */}
            <tr>
              <td style={{ ...tdBase, textAlign: 'center', color: TEXT_MED }}>REF-001</td>
              <td style={{ ...tdBase, fontWeight: 700 }}>Mini-pelle 1.5T — Location journalière</td>
              <td style={{ ...tdBase, textAlign: 'center' }}>5</td>
              <td style={{ ...tdBase, textAlign: 'right' }}>146,00</td>
              <td style={{ ...tdBase, textAlign: 'center', color: TEXT_MED }}>—</td>
              <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700 }}>730,00 €</td>
            </tr>
            {/* Ligne description (span col 0-1, sans bordures horizontales) */}
            <tr>
              <td colSpan={2} style={{
                padding: '2px 8px', fontSize: 7, color: TEXT_MED, fontStyle: 'italic',
                borderLeft: `0.5px solid ${BORDER}`, borderRight: `0.5px solid ${BORDER}`,
                borderBottom: `0.5px solid ${BORDER}`,
              }}>
                • Inclus : livraison et reprise sur chantier<br />
                • Carburant non inclus
              </td>
              <td colSpan={4} style={{
                borderRight: `0.5px solid ${BORDER}`,
                borderBottom: `0.5px solid ${BORDER}`,
              }} />
            </tr>
            {/* 2e ligne article */}
            <tr>
              <td style={{ ...tdBase, textAlign: 'center', color: TEXT_MED }}>ASS-001</td>
              <td style={{ ...tdBase, fontStyle: 'italic', color: TEXT_MED }}>Assurance — Renonciation à recours (730,00 € × 8 %)</td>
              <td style={{ ...tdBase, textAlign: 'center' }}>1</td>
              <td style={{ ...tdBase, textAlign: 'right' }}>58,40</td>
              <td style={{ ...tdBase, textAlign: 'center', color: TEXT_MED }}>—</td>
              <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700 }}>58,40 €</td>
            </tr>
          </tbody>
        </table>

        {/* ══ 5. TOTAUX (alignés à droite) ══ */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <table style={{ borderCollapse: 'collapse', minWidth: 170 }}>
            <tbody>
              <tr>
                <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: 8, color: TEXT_MED }}>Total HT</td>
                <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: 8, fontWeight: 700 }}>788,40 €</td>
              </tr>
              <tr>
                <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: 8, color: TEXT_MED }}>TVA 20 %</td>
                <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: 8, fontWeight: 700 }}>157,68 €</td>
              </tr>
              <tr style={{ borderTop: `1px solid ${PRIMARY}`, background: 'white' }}>
                <td style={{ padding: '4px 6px', textAlign: 'right', fontSize: 9.5, fontWeight: 700, color: PRIMARY, border: `0.5px solid ${BORDER}` }}>TOTAL TTC</td>
                <td style={{ padding: '4px 6px', textAlign: 'right', fontSize: 9.5, fontWeight: 700, color: PRIMARY, border: `0.5px solid ${BORDER}` }}>946,08 €</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* ══ 6. CONDITIONS (fond gris) — zones éditables ══ */}
        <table style={{ width: '100%', borderCollapse: 'collapse', background: GREY_LIGHT, border: `0.5px solid ${BORDER}` }}>
          <tbody>
            <tr>
              <td style={{ padding: '5px 8px', width: '30%', fontSize: 8, fontWeight: 700, borderRight: `0.3px solid ${BORDER}` }}>
                Conditions de règlement
              </td>
              <td style={{ padding: '5px 8px', fontSize: 8, color: TEXT_MED, background: '#FFFBEB', border: '1px dashed #F59E0B' }}>
                {get('conditions_paiement_texte') || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>Non renseigné (utilise les conditions du client)</span>}
              </td>
            </tr>
            <tr>
              <td style={{ padding: '5px 8px', fontSize: 8, fontWeight: 700, borderRight: `0.3px solid ${BORDER}`, borderTop: `0.3px solid ${BORDER}` }}>
                Validité de l'offre
              </td>
              <td style={{ padding: '5px 8px', fontSize: 8, color: TEXT_MED, background: '#FFFBEB', border: '1px dashed #F59E0B', borderTop: `0.3px solid ${BORDER}` }}>
                {get('validite_texte', 'Offre valable 30 jours à compter de la date d\'émission.')}
              </td>
            </tr>
          </tbody>
        </table>

        {/* ══ 7. SIGNATURE BON DE COMMANDE ══ */}
        <div style={{ border: `0.5px solid ${BORDER}`, padding: '6px 8px' }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <div style={{ flex: '0 0 65%' }}>
              <p style={{ fontSize: 7.5, fontWeight: 700, color: TEXT_MED, marginBottom: 4, background: '#FFFBEB', border: '1px dashed #F59E0B', padding: '2px 5px', display: 'inline-block' }}>
                {get('label_signature', 'Bon pour accord, lu et approuvé')}
              </p>
              <div style={{ height: 28, borderBottom: `1px solid ${BORDER}`, marginTop: 4 }} />
            </div>
            <div style={{ flex: '0 0 35%' }}>
              <p style={{ fontSize: 7.5, fontWeight: 700, color: TEXT_MED, marginBottom: 4 }}>Date :</p>
              <div style={{ height: 28, borderBottom: `1px solid ${BORDER}`, marginTop: 4 }} />
            </div>
          </div>
        </div>

        {/* ══ 8. PIED DE PAGE ══ */}
        {get('mentions_legales') && (
          <div style={{ borderTop: `0.5px solid ${BORDER}`, paddingTop: 4, background: '#FFFBEB', border: '1px dashed #F59E0B', padding: '4px 6px', borderRadius: 2 }}>
            <p style={{ color: TEXT_MUTED, fontSize: 7, whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>
              {get('mentions_legales')}
            </p>
          </div>
        )}
        <div style={{ borderTop: `0.5px solid ${BORDER}`, paddingTop: 4 }}>
          <p style={{ color: TEXT_MUTED, fontSize: 7 }}>Document généré automatiquement par Bedee.</p>
        </div>

      </div>
    </div>
  );
}

// ─── Aperçu PDF — Contrat de Location (design v2 - correspond au PDF généré) ──

function ApercuContratLocation({ champs = {} }) {
  const get = (key, def = '') => (champs[key]?.valeur ?? def);

  const HEADER_COLOR = '#374151';   // gris foncé — identique au PDF
  const ACCENT_COLOR = '#D97706';   // orange agence (exemple)
  const ACCENT_LITE  = '#FEF3C7';

  return (
    <div className="rounded-xl border overflow-hidden text-xs"
      style={{ borderColor: '#D1D5DB', fontFamily: 'Helvetica, Arial, sans-serif', lineHeight: 1.5 }}>

      {/* ── En-tête : Logo | CONTRAT DE LOCATION | Numéro ── */}
      <div className="flex items-center justify-between px-5 py-3 border-b"
        style={{ background: HEADER_COLOR, borderColor: HEADER_COLOR }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded flex items-center justify-center text-xs font-bold"
            style={{ background: '#ffffff20', color: 'white', border: '1px dashed #ffffff60' }}>
            LOGO
          </div>
          <div>
            <p className="font-bold tracking-widest" style={{ color: 'white', fontSize: 11 }}>CONTRAT DE LOCATION</p>
            <p style={{ color: '#D1D5DB', fontSize: 8 }}>LOC971/2026/XXXX — Signé le 06/04/2026</p>
          </div>
        </div>
        <div className="text-right" style={{ color: '#D1D5DB', fontSize: 8 }}>
          <div className="w-10 h-10 border flex items-center justify-center rounded"
            style={{ borderColor: '#ffffff40', fontSize: 7 }}>QR</div>
        </div>
      </div>

      <div className="px-5 py-4 space-y-3" style={{ background: 'white' }}>

        {/* ── Loueur / Locataire ── */}
        <div className="grid grid-cols-2 gap-3">
          {[
            { titre: 'LOUEUR', nom: 'LOCA SYSTEM 971', ligne1: '12 Rue des Engins', ligne2: '97400 Saint-Denis', tel: '02 62 XX XX XX' },
            { titre: 'LOCATAIRE', nom: 'Entreprise Martin SAS', ligne1: '5 Av. du Bâtiment', ligne2: '97200 Fort-de-France', tel: '' },
          ].map(({ titre, nom, ligne1, ligne2, tel }) => (
            <div key={titre} className="p-2.5 rounded-lg" style={{ background: '#F9FAFB', border: '1px solid #E5E7EB' }}>
              <p className="font-bold tracking-widest mb-1" style={{ color: ACCENT_COLOR, fontSize: 7 }}>{titre}</p>
              <p className="font-semibold" style={{ color: '#111827', fontSize: 9 }}>{nom}</p>
              <p style={{ color: '#6B7280', fontSize: 8 }}>{ligne1}</p>
              <p style={{ color: '#6B7280', fontSize: 8 }}>{ligne2}</p>
              {tel && <p style={{ color: '#6B7280', fontSize: 8 }}>{tel}</p>}
            </div>
          ))}
        </div>

        {/* ── Vos références ── */}
        <div className="px-2.5 py-1.5 rounded" style={{ background: ACCENT_LITE, fontSize: 8 }}>
          <span className="font-semibold" style={{ color: ACCENT_COLOR }}>Vos références (N° BC) : </span>
          <span style={{ color: '#374151' }}>BC-2026-0042</span>
        </div>

        {/* ── Tableau matériels ── */}
        <div>
          <table className="w-full" style={{ borderCollapse: 'collapse', fontSize: 8.5 }}>
            <thead>
              <tr style={{ background: HEADER_COLOR }}>
                {['Désignation', 'Jours', 'PU HT', 'Total HT'].map((h, i) => (
                  <th key={h} style={{
                    padding: '4px 6px', color: 'white', fontWeight: 700, textAlign: i === 0 ? 'left' : 'center',
                    fontSize: 8
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Ligne matériel — fond légèrement coloré */}
              <tr style={{ background: '#FEF9E7' }}>
                <td style={{ padding: '4px 6px', color: '#111827', fontWeight: 600 }}>Pelles à chenilles – de 2 à 3 t</td>
                <td style={{ padding: '4px 6px', color: '#374151', textAlign: 'center' }}>7</td>
                <td style={{ padding: '4px 6px', color: '#374151', textAlign: 'right' }}>146,00/j</td>
                <td style={{ padding: '4px 6px', color: '#111827', fontWeight: 600, textAlign: 'right' }}>1 022,00 €</td>
              </tr>
              {/* Détail — N° parc / dates / calcul */}
              <tr>
                <td colSpan={4} style={{ padding: '3px 8px', background: '#FAFAFA', fontSize: 7.5, color: '#6B7280' }}>
                  <span className="mr-3"><b>N° parc</b> : 971-042</span>
                  <span className="mr-3"><b>N° immat</b> : YZ-971-AA</span>
                  <span><b>N° série</b> : CAT-2024-042</span><br />
                  <span style={{ fontStyle: 'italic' }}>Du 28/03/2026 au 08/04/2026 — 10 j calendrier − 3 j suspension = 7 j × 146,00/j</span>
                </td>
              </tr>
              {/* ↳ Suspension */}
              <tr>
                <td colSpan={4} style={{ padding: '2px 14px', background: '#FFF5F5', fontSize: 7.5 }}>
                  <span style={{ color: '#DC2626' }}>↳ −3 j Intempéries (02/04 → 04/04)</span>
                </td>
              </tr>
              {/* Ligne assurance */}
              <tr style={{ background: '#F9FAFB' }}>
                <td style={{ padding: '4px 6px', color: '#374151', fontStyle: 'italic' }}>
                  Assurance — Renonciation à recours (1 022,00 € × 8 %)
                </td>
                <td style={{ padding: '4px 6px', textAlign: 'center', color: '#374151' }}>1</td>
                <td style={{ padding: '4px 6px', textAlign: 'right', color: '#374151' }}>81,76</td>
                <td style={{ padding: '4px 6px', textAlign: 'right', color: '#374151' }}>81,76 €</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* ── Totaux ── */}
        <div className="rounded p-2.5 text-right space-y-0.5"
          style={{ background: ACCENT_LITE, border: `1px solid #FDE68A` }}>
          <p style={{ color: '#6B7280', fontSize: 8 }}>Total HT : <strong style={{ color: '#111827' }}>1 103,76 €</strong></p>
          <p style={{ color: '#6B7280', fontSize: 8 }}>TVA 20 % : <strong style={{ color: '#111827' }}>220,75 €</strong></p>
          <p style={{ fontWeight: 700, color: '#111827', fontSize: 9 }}>TOTAL TTC : 1 324,51 €</p>
        </div>

        {/* ── Conditions (zones éditables) ── */}
        {get('preambule') && (
          <div className="p-2.5 rounded-lg" style={{ background: '#FFFBEB', border: '1.5px dashed #F59E0B' }}>
            <p style={{ color: '#92400E', fontSize: 7.5, fontWeight: 700, marginBottom: 2 }}>CONDITIONS DE LOCATION</p>
            <p style={{ color: '#374151', whiteSpace: 'pre-wrap' }}>{get('preambule')}</p>
          </div>
        )}
        {get('conditions_generales') && (
          <div className="p-2.5 rounded-lg" style={{ background: '#FFF9C4', border: '1px solid #FDE68A' }}>
            <p style={{ color: '#374151', whiteSpace: 'pre-wrap' }}>{get('conditions_generales')}</p>
          </div>
        )}
        {get('mention_caution') && (
          <div className="p-2 rounded" style={{ background: '#FFFBEB', border: '1px solid #FDE68A' }}>
            <p style={{ color: '#374151', fontStyle: 'italic', fontSize: 8 }}>{get('mention_caution')}</p>
          </div>
        )}

        {/* ── Signature ── */}
        <div>
          <p className="font-bold tracking-widest mb-1.5" style={{ color: ACCENT_COLOR, fontSize: 7 }}>SIGNATURE DU CLIENT</p>
          <table className="w-full" style={{ borderCollapse: 'collapse', fontSize: 8 }}>
            <thead>
              <tr style={{ background: '#F9FAFB', color: '#6B7280', fontSize: 7.5 }}>
                <th style={{ padding: '3px 6px', border: '1px solid #E5E7EB', textAlign: 'left' }}>Fait à</th>
                <th style={{ padding: '3px 6px', border: '1px solid #E5E7EB', textAlign: 'left' }}>Le</th>
                <th style={{ padding: '3px 6px', border: '1px solid #E5E7EB', textAlign: 'left' }}>{get('label_signature', 'Bon pour accord, lu et approuvé')}</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ height: 28 }}>
                <td style={{ border: '1px solid #E5E7EB', padding: '3px 6px' }}></td>
                <td style={{ border: '1px solid #E5E7EB', padding: '3px 6px', color: '#374151' }}>06/04/2026</td>
                <td style={{ border: '1px solid #E5E7EB', padding: '3px 6px' }}></td>
              </tr>
            </tbody>
          </table>
        </div>

        {get('mentions_legales') && (
          <div className="pt-1 border-t" style={{ borderColor: '#E5E7EB' }}>
            <p style={{ color: '#9CA3AF', fontSize: 7.5, whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>{get('mentions_legales')}</p>
          </div>
        )}
        <p style={{ color: '#9CA3AF', fontSize: 7.5 }}>Document généré le 06/04/2026 à 14:30 — LOC971/2026/XXXX</p>
      </div>
    </div>
  );
}

// ─── Aperçu PDF — Bon de Livraison ───────────────────────────────────────────

function ApercuBonLivraison({ champs = {} }) {
  const get = (key, def = '') => (champs[key]?.valeur ?? def);

  return (
    <div className="rounded-xl border overflow-hidden text-xs"
      style={{ borderColor: '#D1D5DB', fontFamily: 'Georgia, serif', lineHeight: 1.6 }}>
      {/* En-tête */}
      <div className="px-6 py-4 border-b" style={{ background: '#1A3A6E' }}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-base font-bold tracking-widest" style={{ color: 'white', fontFamily: 'Helvetica, sans-serif' }}>BON DE LIVRAISON</p>
            <p style={{ color: '#93C5FD', fontFamily: 'Helvetica, sans-serif', fontSize: 10 }}>LOC-2026-XXXX-BL1</p>
          </div>
          <div className="text-right" style={{ color: '#93C5FD', fontSize: 9 }}>
            <p>[Logo de l'agence]</p>
          </div>
        </div>
      </div>

      <div className="px-6 py-4 space-y-3" style={{ background: 'white' }}>
        {/* Infos */}
        <div className="grid grid-cols-2 gap-3 p-3 rounded-lg" style={{ background: '#F9FAFB', border: '1px solid #E5E7EB' }}>
          <div><p style={{ color: '#9CA3AF', fontSize: 8, fontFamily: 'sans-serif' }}>CLIENT</p><p style={{ color: '#111827', fontWeight: 600 }}>Entreprise Martin SAS</p></div>
          <div><p style={{ color: '#9CA3AF', fontSize: 8, fontFamily: 'sans-serif' }}>CONTRAT</p><p style={{ color: '#111827' }}>LOC-2026-XXXX</p></div>
          <div><p style={{ color: '#9CA3AF', fontSize: 8, fontFamily: 'sans-serif' }}>MATÉRIEL</p><p style={{ color: '#111827', fontWeight: 600 }}>Mini-pelle 1.5T</p></div>
          <div><p style={{ color: '#9CA3AF', fontSize: 8, fontFamily: 'sans-serif' }}>PÉRIODE</p><p style={{ color: '#111827' }}>28/03 → 04/04/2026</p></div>
        </div>

        {/* Introduction — zone éditable */}
        {get('introduction') && (
          <div className="p-2 rounded" style={{ background: '#FFFBEB', border: '1.5px dashed #F59E0B' }}>
            <p style={{ color: '#374151', fontStyle: 'italic' }}>{get('introduction')}</p>
          </div>
        )}

        {/* Critères de contrôle */}
        <div>
          <p style={{ color: '#1A3A6E', fontFamily: 'sans-serif', fontWeight: 700, fontSize: 8, letterSpacing: 1, marginBottom: 4 }}>CONTRÔLE DU MATÉRIEL</p>
          <table className="w-full" style={{ borderCollapse: 'collapse', fontSize: 9 }}>
            <thead>
              <tr style={{ background: '#F9FAFB' }}>
                {['Critère', 'Type', 'Valeur constatée'].map(h => (
                  <th key={h} style={{ padding: '4px 6px', textAlign: 'left', color: '#6B7280', fontWeight: 600, borderBottom: '1px solid #E5E7EB' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                ['Niveau carburant', 'Niveau', '75%'],
                ['État général', 'Sélection', 'Bon état'],
                ['Kilométrage', 'Compteur', '1 234 km'],
              ].map(([c, t, v]) => (
                <tr key={c}>
                  <td style={{ padding: '4px 6px', color: '#111827' }}>{c}</td>
                  <td style={{ padding: '4px 6px', color: '#6B7280' }}>{t}</td>
                  <td style={{ padding: '4px 6px', color: '#374151', fontWeight: 600 }}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* État matériel — zone éditable */}
        {get('texte_etat_materiel') && (
          <div className="p-3 rounded-lg" style={{ background: '#FFF9C4', border: '1px solid #FDE68A' }}>
            <p style={{ color: '#374151', whiteSpace: 'pre-wrap' }}>{get('texte_etat_materiel')}</p>
          </div>
        )}

        {/* Signatures */}
        <div className="grid grid-cols-2 gap-4 pt-2">
          {[
            { key: 'label_signature_client', def: 'Signature du client (bon pour réception)' },
            { key: 'label_signature_agent', def: "Signature de l'agent de livraison" },
          ].map(({ key, def }) => (
            <div key={key} className="p-2 rounded border" style={{ borderColor: '#E5E7EB' }}>
              <p style={{ color: '#1A3A6E', fontFamily: 'sans-serif', fontWeight: 700, fontSize: 8, marginBottom: 2 }}>
                {get(key, def).toUpperCase()}
              </p>
              <div style={{ height: 32, borderBottom: '1px solid #D1D5DB', marginBottom: 2 }}></div>
              <p style={{ color: '#9CA3AF', fontSize: 8 }}>Nom :</p>
            </div>
          ))}
        </div>

        {/* Mentions légales */}
        {get('mentions_legales') && (
          <div className="pt-1 border-t" style={{ borderColor: '#E5E7EB' }}>
            <p style={{ color: '#9CA3AF', fontSize: 8, whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>{get('mentions_legales')}</p>
          </div>
        )}

        <p style={{ color: '#9CA3AF', fontSize: 8, fontFamily: 'sans-serif' }}>
          Document généré le 28/03/2026 à 14:30 — Agent : Jean Dupont
        </p>
      </div>
    </div>
  );
}

// ─── Aperçu PDF — Bon de commande fournisseur ────────────────────────────────

function ApercuDevisAchat({ champs = {} }) {
  const get = (key, def = '') => (champs[key]?.valeur ?? def);
  const PRIMARY = '#1A3A6E';
  const BORDER  = '#CCCCCC';

  return (
    <div style={{ background: 'white', fontFamily: 'Helvetica, Arial, sans-serif', fontSize: 8, lineHeight: 1.45, border: `1px solid ${BORDER}`, borderRadius: 4, overflow: 'hidden' }}>
      {/* En-tête */}
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 16px 10px', borderBottom: `0.5px solid ${BORDER}` }}>
        <div style={{ width: 72, height: 40, background: '#F3F4F6', border: '1px dashed #D1D5DB', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: 7, color: '#9CA3AF' }}>LOGO</span>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: PRIMARY }}>BON DE COMMANDE</div>
          <div style={{ fontWeight: 700, fontSize: 10, color: PRIMARY }}>N° BC-2026-XXXX</div>
          <div style={{ color: '#888', fontSize: 8 }}>Date : 28/03/2026</div>
        </div>
      </div>
      {/* Blocs émetteur / fournisseur */}
      <div style={{ display: 'flex', gap: 12, padding: '10px 16px 8px' }}>
        {[['Notre société', 'Agence Bedee\n12 rue de la Paix\n75001 Paris'], ['Fournisseur', 'Fournitures Pro SAS\n8 avenue de Lyon\n69002 Lyon']].map(([title, addr]) => (
          <div key={title} style={{ flex: 1, border: `0.5px solid ${BORDER}`, borderRadius: 3, padding: '6px 8px' }}>
            <div style={{ fontWeight: 700, fontSize: 9, color: PRIMARY, marginBottom: 4 }}>{title}</div>
            {addr.split('\n').map((l, i) => <div key={i} style={{ color: '#444', fontSize: 8 }}>{l}</div>)}
          </div>
        ))}
      </div>
      {/* Tableau */}
      <div style={{ padding: '0 16px 10px' }}>
        {get('texte_introduction') && (
          <div style={{ background: '#FFFBEB', border: '1.5px dashed #F59E0B', borderRadius: 3, padding: '4px 8px', marginBottom: 8 }}>
            <span style={{ color: '#374151', fontStyle: 'italic', fontSize: 8 }}>{get('texte_introduction')}</span>
          </div>
        )}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 8 }}>
          <thead>
            <tr style={{ background: '#EEEEEE' }}>
              {['Référence', 'Désignation', 'Qté', 'PA HT', 'TVA', 'Total HT'].map(h => (
                <th key={h} style={{ padding: '4px 5px', border: `0.5px solid ${BORDER}`, color: '#000', fontWeight: 700, textAlign: h === 'Désignation' ? 'left' : 'center' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[['ART-001', 'Matériau XYZ', '10', '45,00 €', '20 %', '450,00 €'], ['ART-002', 'Prestation de pose', '2', '150,00 €', '20 %', '300,00 €']].map((row, i) => (
              <tr key={i} style={{ background: i % 2 ? '#F9FAFB' : 'white' }}>
                {row.map((v, j) => <td key={j} style={{ padding: '4px 5px', border: `0.5px solid ${BORDER}`, textAlign: j === 1 ? 'left' : 'right', color: '#444' }}>{v}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
          <div style={{ textAlign: 'right', fontSize: 8 }}>
            <div style={{ color: '#444' }}>Total HT : <strong>750,00 €</strong></div>
            <div style={{ color: '#444' }}>TVA 20 % : 150,00 €</div>
            <div style={{ fontWeight: 700, fontSize: 10, color: PRIMARY }}>TOTAL TTC : 900,00 €</div>
          </div>
        </div>
      </div>
      {/* Bloc validé par */}
      <div style={{ padding: '8px 16px', borderTop: `0.5px solid ${BORDER}` }}>
        <div style={{ fontWeight: 700, fontSize: 8.5, color: '#888', marginBottom: 6 }}>
          {get('label_signature', 'Validé par :')}
        </div>
        <div style={{ border: `0.5px solid ${BORDER}`, borderRadius: 3, height: 36, width: '55%' }} />
      </div>
    </div>
  );
}

// ─── Composants d'aperçu par type_code ───────────────────────────────────────

const APERCUS = {
  ventes:      ApercuDevisVente,
  contrat_location: ApercuContratLocation,
  bon_livraison:    ApercuBonLivraison,
  devis_achat:      ApercuDevisAchat,
};

const TYPE_ICONS = {
  ventes:      Receipt,
  contrat_location: FileText,
  bon_livraison:    Package,
  devis_achat:      ShoppingCart,
};

// ─── Éditeur de champs ────────────────────────────────────────────────────────

function FieldEditor({ champs, onChange }) {
  if (!champs || Object.keys(champs).length === 0) return null;
  return (
    <div className="space-y-3">
      {Object.entries(champs).map(([key, field]) => (
        <div key={key}>
          <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>
            {field.label}
          </label>
          {field.type === 'textarea' ? (
            <textarea
              value={field.valeur || ''}
              onChange={e => onChange(key, e.target.value)}
              rows={3}
              className="w-full px-3 py-2 rounded-xl border text-sm resize-none"
              style={{ borderColor: 'var(--border)' }}
              data-testid={`champ-${key}`}
              placeholder={`${field.label}...`}
            />
          ) : (
            <input
              type="text"
              value={field.valeur || ''}
              onChange={e => onChange(key, e.target.value)}
              className="w-full px-3 py-2 rounded-xl border text-sm"
              style={{ borderColor: 'var(--border)' }}
              data-testid={`champ-${key}`}
              placeholder={`${field.label}...`}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Page principale ───────────────────────────────────────────────────────────

export default function TypesDocuments() {
  const [types, setTypes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editedChamps, setEditedChamps] = useState({});
  const [editedDescription, setEditedDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [previewLive, setPreviewLive] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getTypesDocuments();
      const items = res.data?.items || [];
      setTypes(items);
      // Sélectionner le premier par défaut
      if (items.length > 0 && !selected) {
        selectType(items[0]);
      }
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const selectType = (t) => {
    setSelected(t);
    setEditedChamps(JSON.parse(JSON.stringify(t.champs || {})));
    setEditedDescription(t.description || '');
    setPreviewLive(JSON.parse(JSON.stringify(t.champs || {})));
  };

  const handleFieldChange = (key, val) => {
    setEditedChamps(prev => ({
      ...prev,
      [key]: { ...prev[key], valeur: val },
    }));
    setPreviewLive(prev => ({
      ...prev,
      [key]: { ...prev[key], valeur: val },
    }));
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await api.updateTypeDocument(selected.type_id, {
        description: editedDescription,
        champs: editedChamps,
      });
      toast.success('Modifications enregistrées');
      setTypes(prev => prev.map(t => t.type_id === selected.type_id ? res.data : t));
      setSelected(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const ApercuComp = selected ? APERCUS[selected.type_code] : null;

  return (
    <div className="flex h-full" style={{  }}>

      {/* Sidebar gauche — liste des types */}
      <div className="flex flex-col border-r" style={{ width: 260, borderColor: 'var(--border)', flexShrink: 0 }}>
        <div className="px-4 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <h1 className="text-base font-bold" style={{ color: 'var(--text)' }}>Types de documents</h1>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>Documents PDF générés par l'application</p>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-5 h-5 rounded-full border-2 border-t-transparent animate-spin"
                style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
            </div>
          ) : types.map(t => {
            const Icon = TYPE_ICONS[t.type_code] || FileText;
            const isSelected = selected?.type_id === t.type_id;
            return (
              <button key={t.type_id}
                data-testid={`type-item-${t.type_code}`}
                onClick={() => selectType(t)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all"
                style={{
                  background: isSelected ? 'var(--primary-50)' : 'transparent',
                  border: `1px solid ${isSelected ? 'var(--primary-solid)' : 'transparent'}`,
                }}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: isSelected ? 'var(--primary-solid)' : 'var(--bg-subtle)' }}>
                  <Icon size={14} style={{ color: isSelected ? 'white' : 'var(--text-muted)' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate" style={{ color: isSelected ? 'var(--primary-solid)' : 'var(--text)' }}>
                    {t.nom}
                  </p>
                </div>
                <ChevronRight size={13} style={{ color: isSelected ? 'var(--primary-solid)' : 'var(--text-disabled)', flexShrink: 0 }} />
              </button>
            );
          })}
        </div>
      </div>

      {/* Contenu principal */}
      {selected ? (
        <div className="flex flex-1 overflow-hidden">

          {/* Panel d'édition */}
          <div className="flex flex-col border-r overflow-y-auto" style={{ width: 340, borderColor: 'var(--border)', flexShrink: 0 }}>
            {/* En-tête */}
            <div className="px-5 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
              <p className="text-xs font-bold uppercase tracking-widest mb-0.5" style={{ color: 'var(--text-muted)' }}>
                {selected.nom}
              </p>
              <p className="text-xs" style={{ color: 'var(--text-disabled)' }}>
                Modifiez les textes — l'aperçu se met à jour en temps réel
              </p>
            </div>

            <div className="p-5 space-y-5 flex-1">
              {/* Description */}
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-muted)' }}>
                  Description du document
                </label>
                <textarea
                  value={editedDescription}
                  onChange={e => setEditedDescription(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 rounded-xl border text-sm resize-none"
                  style={{ borderColor: 'var(--border)' }}
                  data-testid="description-input"
                  placeholder="Description..."
                />
              </div>

              <hr style={{ borderColor: 'var(--border)' }} />

              {/* Zones de texte personnalisables */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Edit2 size={13} style={{ color: 'var(--primary-solid)' }} />
                  <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
                    Zones de texte personnalisables
                  </p>
                </div>
                <div className="text-xs rounded-xl px-3 py-2 mb-3"
                  style={{ background: '#FFFBEB', color: '#92400E', border: '1px solid #FDE68A' }}>
                  Les zones en surbrillance jaune dans l'aperçu correspondent aux textes modifiables ci-dessous.
                </div>
                <FieldEditor
                  champs={editedChamps}
                  onChange={handleFieldChange}
                />
              </div>
            </div>

            {/* Bouton enregistrer */}
            <div className="px-5 py-4 border-t" style={{ borderColor: 'var(--border)' }}>
              <button onClick={handleSave} disabled={saving}
                data-testid="save-btn"
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: 'var(--primary-gradient)' }}>
                <Save size={14} />
                {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </button>
            </div>
          </div>

          {/* Aperçu PDF en temps réel */}
          <div className="flex-1 overflow-y-auto p-6" style={{ background: 'var(--bg-subtle)' }}>
            <div className="flex items-center gap-2 mb-4">
              <FileText size={14} style={{ color: 'var(--text-muted)' }} />
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
                Aperçu — {selected.nom}
              </p>
              <span className="text-xs px-2 py-0.5 rounded-full"
                style={{ background: '#FFFBEB', color: '#92400E', border: '1px solid #FDE68A' }}>
                Les zones surlignées sont modifiables
              </span>
            </div>
            <div style={{ maxWidth: 700 }}>
              {ApercuComp && <ApercuComp champs={previewLive} />}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center" style={{ color: 'var(--text-disabled)' }}>
          <div className="text-center">
            <FileText size={36} style={{ opacity: 0.3, margin: '0 auto 8px' }} />
            <p className="text-sm">Sélectionnez un type de document</p>
          </div>
        </div>
      )}
    </div>
  );
}
